import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { tracker } from '../../../tracker';
import { ThirdPartyServiceOpenedEvent } from '../../../tracker/events/third-party-service-opened';
import { SvgExternalLink } from '../../../icons';
import { Asset, SeriesAsset } from '../../../queries/ats/titles/types/asset';
import { SvodSource, ThirdPartySource } from '../../../queries/ats/titles/types/sources';
import { getSeasons } from '../../../queries/ats/titles/utils/array-utils';
import { localizedRelativeTime } from '../../../utils/ats-details-utils';
import { useDetails } from '../details-context';
import { CallToAction } from './call-to-action-button';
import { ThirdPartySourceDeepLink } from './third-party-source-deep-link';
import { useDetailsSearchParams } from '../use-details-search-params';

interface DeepLinkCtaProps {
  asset: Asset;
  source: ThirdPartySource | SvodSource;
  subtitle?: string;
}

function DeepLinkCta({ asset, source, subtitle }: DeepLinkCtaProps) {
  const { t } = useTranslation();

  const withLabel = (text: string) => `${text} ${source.name}`;
  const { open, play } = source.links;
  const link = open || play;

  const { page, section } = useDetailsSearchParams();

  if (link === null) {
    throw new Error(`No link found for third party source ${source.name}`);
  }

  return (
    <CallToAction
      onClick={() => {
        if (source.type === 'thirdparty') {
          tracker.track(new ThirdPartyServiceOpenedEvent(asset, source, page, section));
        }
      }}
      text={{
        primary: withLabel(t('details:open-in')),
        secondary: subtitle,
      }}
      image={{
        src: source.image,
        alt: source.name,
      }}
      icon={<SvgExternalLink />}
      href={link}
    />
  );
}

interface ThirdPartyCallToActionProps {
  asset: Asset;
  source: ThirdPartySource;
}

export function ThirdPartyCallToAction({ asset, source }: ThirdPartyCallToActionProps) {
  if (asset.type === 'series') {
    return <ThirdPartyCallToActionSeries asset={asset} source={source} />;
  }

  const isFuture = moment().isBefore(source.availableFrom);
  const subtitle = isFuture ? localizedRelativeTime(source.availableFrom, 'airtime') : undefined;

  if (!source.isSubscribed) {
    return <ThirdPartySourceDeepLink asset={asset} source={source} subtitle={subtitle} />;
  }

  return <DeepLinkCta asset={asset} source={source} subtitle={subtitle} />;
}

interface ThirdPartyCallToActionSeriesProps {
  asset: SeriesAsset;
  source: ThirdPartySource;
}

function ThirdPartyCallToActionSeries({ asset, source }: ThirdPartyCallToActionSeriesProps) {
  const { selectedContext } = useDetails('ThirdPartyCallToActionSeries');
  const { t } = useTranslation();
  const episodes = selectedContext.episodes(asset);
  const seasons = getSeasons(episodes);

  const isFuture = moment().isBefore(source.availableFrom);
  const subtitle = isFuture
    ? localizedRelativeTime(source.availableFrom, 'airtime')
    : t('common:seasons', { count: seasons.length });

  if (!source.isSubscribed) {
    return <ThirdPartySourceDeepLink asset={asset} source={source} subtitle={subtitle} />;
  }

  return <DeepLinkCta asset={asset} source={source} subtitle={subtitle} />;
}
